/**
 * 태그 편집 — 화면·콘텐츠.
 *
 * 캠페인은 "전체" 아니면 **태그**로 대상을 고른다. 그런데 콘솔에는 태그를 붙일 곳이 없어서
 * (연동 API 로만 붙었다) 캠페인 편집기의 "태그로 지정" 이 사실상 쓸 수 없는 선택지였다.
 *
 * **위치와 같이 드롭다운으로 고른다(2026-10-20 사용자 지시).** 이미 쓰이는 태그를 고르는 것이 기본이고, 적는 칸은
 * [새 태그 직접 입력…] 을 골랐을 때만 나온다(`LocationPicker` 와 같은 꼴). 적기만 하던 때에는 "로비" 와 "로비 " 처럼
 * 한 글자 다른 태그가 생겨 캠페인 대상에서 화면이 조용히 빠졌다. 적는 칸에서는 Enter(또는 쉼표)로 더하고 여러 개는
 * 쉼표로 한 번에. × 로 뺀다.
 *
 * 언제 저장하는지는 부르는 쪽이 정한다 — 콘텐츠 상세는 붙이거나 빼는 즉시, 화면 상세는 [편집] 안에서 모았다가 [저장].
 * 후자를 위해 적는 칸의 글자도 부르는 쪽이 쥘 수 있다(`text`) — Enter 없이 [저장] 을 누르면 그 글자도 함께 붙인다.
 */
import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { compareLocationNames } from './locationOptions.js';
import { isComposingKey } from './ui.js';

/** 서버 `tagSchema` · `displayPatchSchema` 와 같은 한도. */
export const TAG_MAX_LENGTH = 40;
export const TAG_MAX_COUNT = 40;

/**
 * [새 태그 직접 입력…] 을 고르면 오는 값. 앞에 공백을 둔 까닭은 위치의 `NEW_LOCATION` 과 같다 — 태그는 다듬어서
 * 늘어놓으므로 앞뒤 공백이 붙은 값은 선택지가 될 수 없어, 실제 태그와 겹치지 않는다.
 */
export const NEW_TAG = ' __new_tag__';

/** 입력 한 줄을 태그 목록에 더한다. 공백을 걷고, 겹치는 것과 빈 것은 버린다. */
export function addTags(current: readonly string[], input: string): string[] {
  const next = [...current];
  for (const raw of input.split(',')) {
    const tag = raw.trim().slice(0, TAG_MAX_LENGTH);
    if (!tag || next.includes(tag)) continue;
    if (next.length >= TAG_MAX_COUNT) break;
    next.push(tag);
  }
  return next;
}

/**
 * 드롭다운에 늘어놓을 태그 — 쓰이는 태그 중 아직 붙지 않은 것. 다듬고, 겹치는 것을 하나로 하고, 가나다순(숫자는
 * 크기대로 — 위치 드롭다운과 같은 순서)으로 세운다.
 */
export function tagChoices(known: readonly string[], current: readonly string[]): string[] {
  const names = new Set<string>();
  for (const name of known) {
    const trimmed = name.trim();
    if (trimmed !== '' && !current.includes(trimmed)) names.add(trimmed);
  }
  return [...names].sort(compareLocationNames);
}

/** 두 태그 목록이 같은가 — 순서까지. 바뀐 것이 없으면 [저장] 이 요청 없이 닫는다. */
export function sameTags(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((tag, at) => tag === b[at]);
}

/** 태그 하나 — `onRemove` 가 있으면 × 로 뺄 수 있다. */
function TagChip({ tag, disabled, onRemove }: { tag: string; disabled?: boolean; onRemove?: () => void }): ReactNode {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 py-0.5 text-[12px] text-slate-700 ${onRemove ? 'pl-2.5 pr-1' : 'px-2.5'}`}
    >
      {tag}
      {onRemove ? (
        <button
          type="button"
          aria-label={`태그 ${tag} 빼기`}
          disabled={disabled}
          onClick={onRemove}
          className="flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
        >
          <X className="h-3 w-3" strokeWidth={2.2} />
        </button>
      ) : null}
    </span>
  );
}

/** 붙은 태그를 보이기만 한다 — 상세의 보기 줄. */
export function TagList({ tags }: { tags: readonly string[] }): ReactNode {
  return (
    <span className="tag-list">
      {tags.map((tag) => (
        <TagChip key={tag} tag={tag} />
      ))}
    </span>
  );
}

export function TagEditor({
  tags,
  known,
  disabled = false,
  text,
  onTextChange,
  onChange,
}: {
  tags: readonly string[];
  /** 서버가 준 "지금 쓰이고 있는 태그" 이름들 — 드롭다운이 고를 값. */
  known: readonly string[];
  disabled?: boolean;
  /** 적는 칸의 글자를 부르는 쪽이 쥘 때. 없으면 여기서 쥔다. */
  text?: string;
  onTextChange?: (text: string) => void;
  onChange: (tags: string[]) => void;
}): ReactNode {
  const [ownText, setOwnText] = useState('');
  const draft = text ?? ownText;
  const setDraft = onTextChange ?? setOwnText;
  /** 적기로 넘어갔는가 — [새 태그 직접 입력…] 을 골랐을 때만 참이다(위치 고르기와 같다). */
  const [typing, setTyping] = useState(false);
  const full = tags.length >= TAG_MAX_COUNT;
  const choices = tagChoices(known, tags);

  const commit = (): void => {
    const next = addTags(tags, draft);
    setDraft('');
    if (next.length !== tags.length) onChange(next);
  };

  return (
    <span className="tag-editor">
      {tags.map((tag) => (
        <TagChip key={tag} tag={tag} disabled={disabled} onRemove={() => onChange(tags.filter((item) => item !== tag))} />
      ))}
      {typing ? (
        <span className="tag-editor-new">
          <input
            className="input input-sm"
            value={draft}
            placeholder="새 태그 (쉼표로 여러 개)"
            aria-label="새 태그"
            maxLength={TAG_MAX_LENGTH * TAG_MAX_COUNT}
            disabled={disabled || full}
            // 사람이 방금 [새 태그 직접 입력…] 을 골라서 나온 칸이다 — 곧바로 적게 한다.
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // 한글 조합 중의 Enter 는 글자를 끝내는 것이다 — 여기서 더하면 마지막 글자가 칸에 남는다.
              if (isComposingKey(event.nativeEvent)) return;
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                commit();
              }
            }}
          />
          {draft.trim() ? (
            <button type="button" className="btn btn-tiny" disabled={disabled || full} onClick={commit}>
              추가
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-tiny"
            disabled={disabled}
            onClick={() => setTyping(false)}
            title="쓰이고 있는 태그 중에서 고릅니다"
          >
            목록에서 선택
          </button>
        </span>
      ) : (
        <select
          className="input input-sm"
          value=""
          aria-label="태그 추가"
          disabled={disabled || full}
          onChange={(event) => {
            const next = event.target.value;
            if (next === NEW_TAG) {
              setTyping(true);
              return;
            }
            // 고른 이름은 그대로 붙인다 — 연동 API 로 붙은 태그에는 쉼표가 있을 수 있어 `addTags` 로 쪼개지 않는다.
            if (next && !tags.includes(next) && !full) onChange([...tags, next]);
          }}
        >
          <option value="">{full ? `태그는 ${TAG_MAX_COUNT}개까지` : '태그 추가…'}</option>
          {choices.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
          <option value={NEW_TAG}>새 태그 직접 입력…</option>
        </select>
      )}
    </span>
  );
}
